import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import CmTable from '@/components/cmTable/index.vue'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { FormType, ObjectFormItem, DateType, FormSize } from '@/components/cmForm/types'
import { TableActOpt, TableOpt } from '@/components/cmTable/types'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useParseTime } from '@/hooks'
import { formatTimeStamp } from '@/utils/time'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'
import { InOutLogService, LogData } from './in-out-log.service'

type InOutLogQueryParams = {
  key_word: string
  start_time?: string
  end_time?: string
  page: number
  limit: number
  event_types?: string
  dateRange: [string, string]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'in-out-log',
  props: {
    currentTab: { default: 'in-out-log' },
    embedDefaults: { default: null }
  },
  setup(__props: any) {

const props = __props

const { t, locale } = useI18n()
const route = useRoute()
const injector = useReflectiveInjector([InOutLogService])
const service = injector.get(InOutLogService)

function getRouteBasedDefaultDate(): [string, string] {
  const defaultStart = dayjs().subtract(30, 'day').format('YYYY-MM-DD')
  const defaultEnd = dayjs().format('YYYY-MM-DD')
  const start = route.query.start_time ? dayjs(Number(route.query.start_time)).format('YYYY-MM-DD') : defaultStart
  const end = route.query.end_time ? dayjs(Number(route.query.end_time)).format('YYYY-MM-DD') : defaultEnd
  return [start, end]
}

const routeDefaultDate = getRouteBasedDefaultDate()
const initialDateRange: [string, string] = props.embedDefaults
  ? [...props.embedDefaults.dateRange]
  : routeDefaultDate
const initialEventTypes = props.embedDefaults
  ? props.embedDefaults.event_types
  : ((route.query.event_types as string | undefined) || undefined)
const [startTime, endTime] = useParseTime(initialDateRange)

const queryParams = reactive<InOutLogQueryParams>({
  key_word: '',
  start_time: startTime != null ? String(startTime) : undefined,
  end_time: endTime != null ? String(endTime) : undefined,
  page: 1,
  limit: 20,
  event_types: initialEventTypes,
  dateRange: [...initialDateRange],
})

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const isCurrentTab = computed(() => props.currentTab === 'in-out-log')

const inOutEventTypeLabel = computed(() => {
  void locale.value
  const keyByType: Record<number, string> = {
    1: 'noticeManage.joinTeamEvent',
    2: 'noticeManage.inOutEventTerminalUninstall',
    3: 'noticeManage.inOutEventTerminalLeave',
    4: 'noticeManage.inOutEventAdminDelete',
    5: 'noticeManage.inOutEventAdminBlacklist',
  }
  return (type: number | string) => {
    const key = keyByType[Number(type)]
    return key ? t(key) : '--'
  }
})

const filterFormColumns = computed<Partial<ObjectFormItem<any, any>>>(() => {
  void locale.value
  return {
    event_types: {
      colOpt: { style: 'flex: 0 0 130px' },
      label: t('noticeManage.eventTypeFilterPlaceholder'),
      type: FormType.Select,
      outSearchFiledFlag: true,
      selectOpt: { clearable: true },
      dictData: [
        { label: t('noticeManage.joinTeamEvent'), value: '1' },
        { label: t('noticeManage.leaveTeamEvent'), value: '2-3-4-5' },
      ],
      events: {
        change: () => void refreshList(),
      },
    },
    dateRange: {
      colOpt: { style: 'flex: 0 0 280px' },
      label: t('noticeManage.filterDateRange'),
      type: FormType.Date,
      dateType: DateType.DateRange,
      outSearchFiledFlag: true,
      events: {
        change: (val: [string, string] | undefined) => {
          if (val) {
            const [start, end] = useParseTime(val)
            queryParams.start_time = start != null ? String(start) : undefined
            queryParams.end_time = end != null ? String(end) : undefined
          } else {
            queryParams.start_time = undefined
            queryParams.end_time = undefined
          }
          void refreshList()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('common.startDate'),
        'end-placeholder': t('common.endDate'),
        clearable: false,
      },
    },
  }
})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: {
      label: t('noticeManage.operation'),
      width: 100,
      align: 'left',
      fixed: 'right',
    },
    isLink: true,
    actionButtons: () => [
      {
        name: t('noticeManage.rawData'),
        type: 'rawData',
        opt: { size: FormSize.Small, type: 'text', class: 'cm-link-light underline' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  const eventTypeLabel = inOutEventTypeLabel.value
  return {
    path: 'list',
    totalPath: 'total',
    height: '100%',
    bodyStyle: 'padding: 16px;border-radius: var(--el-border-radius-large);background: #fff',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    selection: false,
    showTip: false,
    columns: [
      {
        label: t('noticeManage.time'),
        prop: 'record_time',
        columnOpt: { minWidth: 140},
        formatter: (_row: LogData, value: number | string) =>
          value ? formatTimeStamp(Number(value), 'YYYY-MM-DD HH:mm') : '--',
      },
      {
        label: t('noticeManage.eventType'),
        prop: 'event_type',
        columnOpt: { minWidth: 120 },
        formatter: (_row: LogData, value: number | string) => eventTypeLabel(value),
      },
      ...computerInfoCols(true, true, t),
    ],
  }
})

const isShowRawData = ref(false)
const currentRow = ref<Record<string, unknown>>({})

function parseEventTypes(eventTypes?: string): number[] | undefined {
  if (!eventTypes) return undefined
  return eventTypes
    .split('-')
    .map((item) => Number(item))
    .filter((item) => !Number.isNaN(item))
}

function dataApi(params: Record<string, unknown>) {
  const p = params as unknown as InOutLogQueryParams
  return service.getLogList({
    key_word: p.key_word || undefined,
    start_time: p.start_time,
    end_time: p.end_time,
    page: Number(params.page),
    limit: Number(params.limit),
    event_types: parseEventTypes(p.event_types),
  })
}

function handleAction({ type, rowItem }: { type: string; rowItem?: LogData }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem as unknown as Record<string, unknown>
    isShowRawData.value = true
  }
}

async function handleSearch(value: string) {
  await nextTick()
  queryParams.key_word = value
  void refreshList()
}

async function handleQuery(form: Partial<InOutLogQueryParams>) {
  if (form.dateRange?.length === 2) {
    const [start, end] = useParseTime(form.dateRange)
    form.start_time = start != null ? String(start) : undefined
    form.end_time = end != null ? String(end) : undefined
  }
  Object.assign(queryParams, form)
  await refreshList()
}

function handleReset() {
  queryParams.key_word = ''
  if (props.embedDefaults) {
    queryParams.event_types = props.embedDefaults.event_types
    queryParams.dateRange = [...props.embedDefaults.dateRange]
  } else {
    queryParams.event_types = undefined
    queryParams.dateRange = [...routeDefaultDate]
  }
  const [start, end] = useParseTime(queryParams.dateRange)
  queryParams.start_time = start != null ? String(start) : undefined
  queryParams.end_time = end != null ? String(end) : undefined
  void refreshList()
}

watch(
  () => props.currentTab,
  async (newValue, oldValue) => {
    if (newValue === 'in-out-log' && oldValue !== 'in-out-log') {
      await refreshList()
    }
  },
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
      "first-loading": isCurrentTab.value,
      "table-opt": tableOpt.value,
      "table-act-opt": tableActOpt.value,
      api: dataApi,
      "page-option": {
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          style: {"padding":"0","background-color":"transparent"},
          modelValue: queryParams,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams) = $event)),
          "form-columns": filterFormColumns.value,
          "show-search": true,
          "sync-search-from-field": "key_word",
          "search-placeholder": _ctx.$t('noticeManage.searchPlaceholderComputerUserIp'),
          "popover-threshold": 2,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, null, 8, ["modelValue", "form-columns", "search-placeholder"])
      ]),
      _: 1
    }, 8, ["modelValue", "first-loading", "table-opt", "table-act-opt"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})